import { loyaltyApi, storage } from '@vmos2/core';
import type { UserPoints } from '@vmos2/loyalty/src/types';
import { useRequest } from '@vmos2/utils';
import { isEqual } from 'lodash';
import type { MutableRefObject } from 'react';
import { useCallback } from 'react';

import { USER_POINTS_CACHE_ID } from '../constants';
import { useSettings } from '../Settings';

const timeout = (ms: number) =>
  new Promise(resolve => {
    window.setTimeout(resolve, ms);
  });

const pointsAreDifferent = (existing?: UserPoints, latest?: UserPoints) => {
  return !!existing && !isEqual(existing, latest);
};

export const useUserPoints = (isLoyaltyEnabled: MutableRefObject<boolean>) => {
  const { isLoyaltyPointsEnabled } = useSettings();
  const apiCache = loyaltyApi.storage.data[USER_POINTS_CACHE_ID];

  const [{ data, isLoading }, fetchUserPoints, resetRequest] = useRequest<UserPoints>(
    useCallback(
      async ({ apiCacheData, cache, requestInProgress }) => {
        if (isLoyaltyPointsEnabled) {
          const response: Exclude<UserPoints, null> = apiCacheData?.payload || await loyaltyApi.get('v2/points', {
            cache,
            id: USER_POINTS_CACHE_ID,
            params: { unconfirmed: true },
          });

          const discount = await storage.getItem<Record<string, any> | null>('discount');

          if (requestInProgress.current) {
            await storage.setItem('discount', { ...discount, userPoints: response });
            isLoyaltyEnabled.current = response.enabled;
          }

          return response;
        }

        return null;
      },
      [isLoyaltyPointsEnabled, isLoyaltyEnabled]
    ),
    // 3600000 = 1 hour
    { apiCache, defaultCache: { override: true, ttl: 3600000 }, preserveDataWhileLoading: true },
  );

  const clearUserPoints = useCallback(async () => {
    resetRequest();
    await loyaltyApi.storage.remove(USER_POINTS_CACHE_ID);
    await storage.removeItem('discount');
  }, [resetRequest]);

  const pollUpdatePoints = useCallback(
    async () => {
      if (!isLoyaltyPointsEnabled) {
        return;
      }

      await timeout(1000);

      const maxRetrials = 3;
      let numberOfCalls = 0;

      const fetchData = async (previousUserPoints?: UserPoints) => {
        const response = await fetchUserPoints();

        numberOfCalls++;

        if (pointsAreDifferent(previousUserPoints, response)) {
          numberOfCalls = maxRetrials;
        } else if (numberOfCalls < maxRetrials) {
          await timeout(500);
          await fetchData(response);
        }
      };

      await fetchData(data);
    },
    [data, fetchUserPoints, isLoyaltyPointsEnabled]
  );

  return {
    clearUserPoints,
    fetchUserPoints,
    isLoadingUserPoints: isLoyaltyPointsEnabled ? isLoading : false,
    pollUpdatePoints,
    userPoints: data,
  };
};
